/* Society membership cart orchestrator.
 *
 * Implements the state machine in docs/state-machine.md. Eligibility is never
 * computed here: all state comes from the Liquid section `society-cart-state`
 * (cart AJAX responses carry no collection or customer data). This script only
 * observes cart mutations, refetches that state, and auto-adds / auto-removes
 * the membership line accordingly.
 *
 * Interceptor contract: this store runs many third-party app embeds sharing
 * window.fetch / XMLHttpRequest (Qikify Slide Cart renders the cart drawer).
 * The wrappers below MUST stay fully transparent — original return values and
 * rejections pass through untouched, response bodies are never consumed, and
 * our reaction runs out-of-band so a failure here can never break the caller.
 *
 * DRY RUN: window.NMSociety.dryRun defaults to true — intended cart mutations
 * are logged to the console instead of executed. Set NMSociety.dryRun = false
 * (or flip DRY_RUN_DEFAULT below) to go live. Debug helpers on window.NMSociety:
 *   evaluateNow(), state(), reset(), markAutoAdded(), suppressed()
 */
(function () {
  'use strict';

  if (window.__nmSocietyInstalled) return;
  window.__nmSocietyInstalled = true;

  // Top window only: app embeds load same-origin iframes that would each run
  // their own orchestrator against the shared cart (observed 4 instances on the
  // preview) — in live mode that is an auto-add race. The drawer and all
  // interceptable cart mutations live in the top window.
  try {
    if (window.top !== window.self) return;
  } catch (e) {
    return; // cross-origin top implies we are framed — same rule applies
  }

  var DRY_RUN_DEFAULT = false;
  var SECTION_ID = 'society-cart-state';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var SUPPRESS_KEY = 'nm_society_suppressed'; // customer-initiated removal: blocks auto-add for the session
  var AUTOADD_KEY = 'nm_society_autoadded';   // WE added the line: only auto-added lines may be auto-removed
  var INCART_KEY = 'nm_society_incart';       // last observed presence, survives reloads for attribution
  var LOOPSTOP_KEY = 'nm_society_loopstop';   // loop guard tripped — deliberately NOT the suppression flag
  var ACTIONCOUNT_KEY = 'nm_society_actions'; // session-wide action tally (layer 3)

  // ---- loop guard thresholds -------------------------------------------------
  // Layer 1 (primary): consecutive orchestrator actions with NO customer cart
  // mutation in between. That is the true signature of a self-sustaining loop —
  // legitimate use is always one action per customer change, so genuine repeated
  // threshold crossings can never reach this.
  var MAX_CONSECUTIVE = 3;
  // Layer 2: rapid-fire backstop, in case layer 1's own attribution is wrong.
  // 8 rather than 6: a real loop cycles in ~2s and still trips well inside the
  // window, but a fast acceptance-checklist run driving several genuine
  // crossings in 30s no longer trips on legitimate activity.
  var RAPID_LIMIT = 8;
  var RAPID_WINDOW_MS = 30000;
  // Layer 3: session ceiling, catches a loop slow enough to evade both windows.
  var SESSION_LIMIT = 20;

  var pendingOwnRemovals = 0; // removals we initiated; disappearances beyond this count are customer-initiated
  var ownMutationExpected = 0; // cart mutations WE are about to issue; consumed by the interceptor
  var actionsSinceCustomerMutation = 0;
  var recentActionTimes = [];
  var actionHistory = [];
  var evalScheduled = false;
  var evalInFlight = false;
  var evalQueued = false;

  function log() {
    try {
      var args = ['[NM Society]'].concat([].slice.call(arguments));
      console.info.apply(console, args);
    } catch (e) { /* logging must never throw */ }
  }

  /* ---------------- sessionStorage flags (per-tab session semantics) ------ */

  function getFlag(key) {
    try { return window.sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }
  function setFlag(key, value) {
    try {
      if (value) window.sessionStorage.setItem(key, '1');
      else window.sessionStorage.removeItem(key);
    } catch (e) { /* private mode: degrade to in-page behaviour */ }
  }
  function getNum(key) {
    try { return parseInt(window.sessionStorage.getItem(key), 10) || 0; } catch (e) { return 0; }
  }
  function setNum(key, value) {
    try { window.sessionStorage.setItem(key, String(value)); } catch (e) { /* non-fatal */ }
  }

  /* ---------------- loop guard -------------------------------------------------
   * If discount attribution ever fails silently, the threshold math can misread
   * and the orchestrator can oscillate add -> remove -> add on a live cart. This
   * detects that and hard-stops. It is deliberately independent of SUPPRESS_KEY:
   * suppression has spec meaning (edge case 1) and must not be conflated with a
   * safety trip.
   */

  function isTripped() { return getFlag(LOOPSTOP_KEY); }

  // Any cart mutation we did NOT initiate proves the customer is driving, so the
  // consecutive-action counter resets. This is what makes genuine repeated
  // threshold crossings safe no matter how many times they happen.
  //
  // The rapid window (layer 2) is cleared for the same reason. A real loop is
  // self-sustaining and produces NO customer mutations, so clearing on customer
  // input keeps the backstop's purpose intact while removing its false-positive
  // risk. That risk became real with the cart drawer: add/remove is now one
  // click with no page navigation, so a shopper (or a tester) can legitimately
  // drive 8 orchestrator actions inside 30s and would otherwise be locked out
  // of auto-add for the rest of the session.
  function noteCustomerMutation() {
    actionsSinceCustomerMutation = 0;
    recentActionTimes = [];
  }

  // Records an action and reports whether it should trip the guard. The tripping
  // action is NOT performed, so at most MAX_CONSECUTIVE-1 actions ever run.
  function recordAction(kind, reason) {
    var now = new Date().getTime();
    actionsSinceCustomerMutation++;
    recentActionTimes.push(now);
    while (recentActionTimes.length && now - recentActionTimes[0] > RAPID_WINDOW_MS) {
      recentActionTimes.shift();
    }
    var sessionCount = getNum(ACTIONCOUNT_KEY) + 1;
    setNum(ACTIONCOUNT_KEY, sessionCount);
    actionHistory.push({ t: now, kind: kind, reason: reason, consecutive: actionsSinceCustomerMutation });
    if (actionHistory.length > 20) actionHistory.shift();

    if (actionsSinceCustomerMutation >= MAX_CONSECUTIVE) {
      return { trip: true, layer: 'consecutive: ' + actionsSinceCustomerMutation + ' orchestrator actions with no customer cart change' };
    }
    if (recentActionTimes.length >= RAPID_LIMIT) {
      return { trip: true, layer: 'rapid: ' + recentActionTimes.length + ' actions within ' + (RAPID_WINDOW_MS / 1000) + 's' };
    }
    if (sessionCount >= SESSION_LIMIT) {
      return { trip: true, layer: 'session ceiling: ' + sessionCount + ' actions this session' };
    }
    return { trip: false };
  }

  function tripGuard(layer) {
    setFlag(LOOPSTOP_KEY, true);
    try {
      console.error('[NM Society] LOOP GUARD TRIPPED (' + layer + '). Auto-add/auto-remove disabled for this session. ' +
        'The cart has been left exactly as-is on purpose — a corrective mutation could itself be the next iteration. History:', actionHistory.slice());
    } catch (e) { /* logging must never throw */ }
    repaintOnly();
  }

  // Read-only repaint so the visible cart matches the real cart after we stop.
  // Uses the multi-section endpoint: no /cart/ path, so it cannot self-trigger.
  function repaintOnly() {
    try {
      var targets = refreshTargets();
      if (!targets.length) return;
      var names = targets.map(function (t) { return t.section; }).join(',');
      window
        .fetch(ROOT + '?sections=' + encodeURIComponent(names), { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sections) { if (sections) applySections(sections, targets); })
        .catch(function () { /* a failed repaint must never throw */ });
    } catch (e) { /* non-fatal */ }
  }

  /* ---------------- transparent cart-mutation interceptors ---------------- */

  function isCartMutationUrl(url) {
    try {
      var u = new URL(String(url), window.location.origin);
      if (u.origin !== window.location.origin) return false;
      return /\/cart\/(add|change|update|clear)(\.js)?$/.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  function installFetchInterceptor() {
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    window.fetch = function () {
      var url = '';
      try {
        var input = arguments[0];
        url = input && typeof input === 'object' && 'url' in input ? input.url : String(input);
      } catch (e) { /* never block the call over URL introspection */ }
      try {
        // Classify synchronously, at call time: performAdd/performRemove raise
        // ownMutationExpected immediately before issuing their request, so a
        // cart mutation arriving with the marker down is customer-driven.
        if (isCartMutationUrl(url)) {
          if (ownMutationExpected > 0) ownMutationExpected--;
          else noteCustomerMutation();
        }
      } catch (e) { /* classification is best-effort */ }
      var result = origFetch.apply(this, arguments);
      try {
        if (isCartMutationUrl(url) && result && typeof result.then === 'function') {
          // Derived promise only: the original is returned untouched, its
          // rejection (if any) still reaches the caller, and we never read the body.
          result.then(function () { scheduleEvaluation(); }, function () { /* caller's error, not ours */ });
        }
      } catch (e) { /* observation is best-effort */ }
      return result;
    };
  }

  function installXhrInterceptor() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto || typeof proto.open !== 'function') return;
    var origOpen = proto.open;
    var origSend = proto.send;
    proto.open = function (method, url) {
      try { this.__nmCartMutation = isCartMutationUrl(url); } catch (e) { /* best-effort */ }
      return origOpen.apply(this, arguments);
    };
    proto.send = function () {
      try {
        if (this.__nmCartMutation) {
          // We never issue our own mutations over XHR, but classify anyway so a
          // future change cannot silently blind the guard.
          if (ownMutationExpected > 0) ownMutationExpected--;
          else noteCustomerMutation();
          this.addEventListener('loadend', function () { scheduleEvaluation(); });
        }
      } catch (e) { /* best-effort */ }
      return origSend.apply(this, arguments);
    };
  }

  /* ---------------- state fetch ------------------------------------------- */

  function fetchState() {
    // Not matched by the interceptor (no /cart/ path) — cannot self-trigger.
    return window
      .fetch(ROOT + '?section_id=' + SECTION_ID, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('state fetch HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('[data-society-cart-state]');
        if (!el) throw new Error('state payload missing from section response');
        return JSON.parse(el.textContent);
      });
  }

  /* ---------------- evaluation (serialized, debounced) -------------------- */

  function scheduleEvaluation() {
    if (evalScheduled) return;
    evalScheduled = true;
    setTimeout(function () {
      evalScheduled = false;
      runEvaluation();
    }, 50);
  }

  function runEvaluation() {
    if (evalInFlight) {
      evalQueued = true;
      return;
    }
    evalInFlight = true;
    fetchState()
      .then(evaluate)
      .catch(function (err) { log('evaluation skipped:', err && err.message ? err.message : err); })
      .then(function () {
        evalInFlight = false;
        if (evalQueued) {
          evalQueued = false;
          runEvaluation();
        }
      });
  }

  // Edge cases 1 vs 2: a disappearance we did not initiate is customer-initiated
  // and suppresses auto-add for the session — but only while other items remain.
  // A fully emptied cart (checkout completion, cart clear) is a fresh start.
  function attributeRemoval(state, inCart) {
    var wasInCart = getFlag(INCART_KEY);
    if (!wasInCart || inCart) return;
    if (pendingOwnRemovals > 0) {
      pendingOwnRemovals--;
      setFlag(AUTOADD_KEY, false);
      log('membership removal completed (orchestrator-initiated — no suppression)');
    } else if (state.item_count > 0) {
      setFlag(SUPPRESS_KEY, true);
      setFlag(AUTOADD_KEY, false);
      log('customer-initiated membership removal — auto-add SUPPRESSED for this session');
    } else {
      setFlag(AUTOADD_KEY, false);
      log('membership gone with empty cart (checkout/clear) — no suppression');
    }
  }

  function evaluate(state) {
    // Stop fetching decisions once tripped. act() is the authoritative gate;
    // this just avoids pointless work and log noise.
    if (isTripped()) return;

    var membership = state.membership || {};
    var inCart = !!membership.line_key;

    attributeRemoval(state, inCart);
    setFlag(INCART_KEY, inCart);

    if (!state.enabled) {
      // Edge case 8: toggle off leaves no residual state.
      setFlag(SUPPRESS_KEY, false);
      if (inCart && getFlag(AUTOADD_KEY)) {
        act('remove', state, 'global toggle off — removing auto-added membership');
      }
      return;
    }

    if (!state.configured) {
      log('membership product not configured in theme settings — idle');
      return;
    }

    if (state.is_member) {
      // Edge cases 4 & 5: tagged customers never see auto-add; a stale
      // auto-added line is removed (double-discount prevention). Manually
      // added membership lines are never touched.
      if (inCart && getFlag(AUTOADD_KEY)) {
        act('remove', state, 'customer has member tag — double-discount prevention');
      }
      return;
    }

    if (inCart) {
      // Edge case 2: automatic removal below threshold — never suppresses.
      // Edge case 7: a manually added membership (no auto-add flag) is left
      // alone at any subtotal — a membership-only cart is a normal purchase.
      if (state.eligible_subtotal < state.threshold && getFlag(AUTOADD_KEY)) {
        act('remove', state, 'eligible subtotal ' + money(state.eligible_subtotal) + ' below threshold ' + money(state.threshold) + ' — automatic removal (no suppression)');
      }
      return;
    }

    if (getFlag(SUPPRESS_KEY)) {
      // Say so out loud. A silent return here is indistinguishable from a broken
      // auto-add, which is exactly how "the membership stopped auto-adding" gets
      // misdiagnosed. Suppression is per-session, set by a customer-initiated
      // removal (edge case 1), cleared by NMSociety.reset() or a new session.
      if (state.eligible_subtotal >= state.threshold) {
        log('auto-add skipped: SUPPRESSED for this session by an earlier customer-initiated ' +
            'removal of the membership. NMSociety.reset() clears it.');
      }
      return;
    }

    if (state.eligible_subtotal >= state.threshold) {
      act('add', state, 'eligible subtotal ' + money(state.eligible_subtotal) + ' reached threshold ' + money(state.threshold));
    }
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------------- actions ----------------------------------------------- */

  function act(kind, state, reason) {
    // Single chokepoint: every cart mutation this script makes passes through
    // here, so one gate makes acting-while-tripped impossible.
    if (isTripped()) {
      log('loop guard active — skipping ' + kind);
      return;
    }
    var verdict = recordAction(kind, reason);
    if (window.NMSociety.dryRun) {
      log('DRY RUN — would ' + (kind === 'add' ? 'auto-add membership (variant ' + state.membership.variant_id + ')' : 'auto-remove membership (line ' + state.membership.line_key + ')') + ': ' + reason);
      // Dry run counts for diagnostics but never trips: nothing mutates, so
      // there is nothing to protect.
      if (verdict.trip) log('DRY RUN — loop guard WOULD have tripped (' + verdict.layer + ')');
      return;
    }
    if (verdict.trip) {
      tripGuard(verdict.layer);
      return; // the tripping action is not performed
    }
    log((kind === 'add' ? 'auto-adding membership: ' : 'auto-removing membership: ') + reason);
    if (kind === 'add') performAdd(state);
    else performRemove(state);
  }

  /* ---------------- cart UI refresh after OUR OWN mutations --------------- */

  // The theme renders its cart UI exactly once, from sections bundled into the
  // response of whichever request made the change (product-form.js line ~27,
  // cart.js getSectionsToRender). Our auto-add lands ~1s later, so without
  // re-rendering here the membership really is in the cart while the page still
  // shows pre-add HTML — the "only appears after a refresh" symptom. Qikify used
  // to hide this by re-rendering its drawer from live cart state.
  //
  // Same Section Rendering API swap the theme itself uses: in-place DOM update,
  // no navigation, no reload. Every target is existence-guarded, so this is safe
  // on a PDP (bubble only) and on the cart page (bubble + items + footer).
  function refreshTargets() {
    var targets = [];
    function add(section, el, selector) {
      if (section && el) targets.push({ section: section, el: el, selector: selector || '.shopify-section' });
    }
    add('cart-icon-bubble', document.getElementById('cart-icon-bubble'));
    add('cart-live-region-text', document.getElementById('cart-live-region-text'));
    add('cart-notification-button', document.getElementById('cart-notification-button'));
    var items = document.getElementById('main-cart-items');
    if (items) add(items.dataset.id, items, '.js-contents');
    // main-cart-footer carries the savings line inside .js-contents, so it
    // re-renders for free with the correct figure.
    var footer = document.getElementById('main-cart-footer');
    if (footer) add(footer.dataset.id, footer, '.js-contents');
    // Cart drawer. Requested by SECTION NAME (unlike main-cart-items/footer,
    // which are bound to the cart template and must be requested by section id),
    // so it can be repainted from any page. Null-guarded: on themes where the
    // drawer is not enabled this adds nothing and changes no behaviour.
    var drawer = document.querySelector('cart-drawer');
    if (drawer) add('cart-drawer', drawer, '.js-contents');
    return targets;
  }

  function withSections(payload, targets) {
    if (targets.length) {
      payload.sections = targets.map(function (t) { return t.section; }).join(',');
      payload.sections_url = window.location.pathname;
    }
    return payload;
  }

  function applySections(sections, targets, itemCount) {
    if (sections) {
      targets.forEach(function (t) {
        try {
          var html = sections[t.section];
          if (!html) return;
          var source = new DOMParser().parseFromString(html, 'text/html').querySelector(t.selector);
          if (!source) return;
          var dest = t.el.querySelector(t.selector) || t.el;
          dest.innerHTML = source.innerHTML;
        } catch (e) { /* a failed repaint must never break the cart */ }
      });
    }
    try {
      if (typeof itemCount === 'number') {
        var cartItems = document.querySelector('cart-items');
        if (cartItems) cartItems.classList.toggle('is-empty', itemCount === 0);
        var footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', itemCount === 0);
      }
    } catch (e) { /* non-fatal */ }

    // Tell the drawer we've already repainted it from this mutation's bundled
    // sections. The drawer independently observes every cart mutation (step 2)
    // and would otherwise fire a second, redundant section fetch ~120ms later —
    // a visible double-render on every auto-add/auto-remove. One request wins:
    // ours already carries the HTML.
    try {
      document.dispatchEvent(new CustomEvent('society:cart-repainted'));
    } catch (e) { /* CustomEvent unsupported — drawer just refetches, still correct */ }
  }

  function performAdd(state) {
    // The membership variant has requires_selling_plan — the add is rejected
    // without the plan id (supplied by the state section, never hardcoded).
    var payload = { id: state.membership.variant_id, quantity: 1 };
    if (state.membership.selling_plan_id) payload.selling_plan = state.membership.selling_plan_id;
    var targets = refreshTargets();
    ownMutationExpected++;
    window
      .fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(withSections(payload, targets))
      })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        setFlag(AUTOADD_KEY, true);
        setFlag(INCART_KEY, true);
        applySections(data && data.sections, targets, data && data.item_count);
        log('membership auto-added');
      })
      .catch(function (err) { log('auto-add failed:', err && err.message ? err.message : err); });
  }

  function performRemove(state) {
    pendingOwnRemovals++;
    var targets = refreshTargets();
    ownMutationExpected++;
    window
      .fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(withSections({ id: state.membership.line_key, quantity: 0 }, targets))
      })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/change HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        applySections(data && data.sections, targets, data && data.item_count);
        log('membership auto-removed');
      })
      .catch(function (err) {
        pendingOwnRemovals = Math.max(0, pendingOwnRemovals - 1);
        log('auto-remove failed:', err && err.message ? err.message : err);
      });
  }

  /* ---------------- public debug API -------------------------------------- */

  window.NMSociety = {
    dryRun: DRY_RUN_DEFAULT,
    evaluateNow: function () { runEvaluation(); },
    state: function () { return fetchState().then(function (s) { log('state', s); return s; }); },
    suppressed: function () { return getFlag(SUPPRESS_KEY); },
    tripped: function () { return isTripped(); },
    history: function () { return actionHistory.slice(); },
    guardCounters: function () {
      return {
        consecutive: actionsSinceCustomerMutation,
        inWindow: recentActionTimes.length,
        sessionTotal: getNum(ACTIONCOUNT_KEY),
        limits: { consecutive: MAX_CONSECUTIVE, rapid: RAPID_LIMIT, windowMs: RAPID_WINDOW_MS, session: SESSION_LIMIT }
      };
    },
    reset: function () {
      setFlag(SUPPRESS_KEY, false);
      setFlag(AUTOADD_KEY, false);
      setFlag(INCART_KEY, false);
      setFlag(LOOPSTOP_KEY, false);
      setNum(ACTIONCOUNT_KEY, 0);
      actionsSinceCustomerMutation = 0;
      recentActionTimes = [];
      actionHistory = [];
      log('session flags and loop guard cleared');
    },
    // Dry-run helper: mark a manually added membership line as if auto-added,
    // so removal transitions can be exercised without live mode.
    markAutoAdded: function () {
      setFlag(AUTOADD_KEY, true);
      log('auto-added flag set (simulation)');
    }
  };

  /* ---------------- boot --------------------------------------------------- */

  try {
    installFetchInterceptor();
    installXhrInterceptor();
  } catch (e) {
    log('interceptor install failed — auto-add inactive this page', e);
  }

  log('initialized' + (DRY_RUN_DEFAULT ? ' (DRY RUN — cart mutations are logged, not executed)' : ''));

  // Initial evaluation on page load, not only after mutations: carts persist
  // across sessions (over-threshold cart on arrival, stale line after tagging,
  // toggle-off cleanup all trigger here).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleEvaluation(); });
  } else {
    scheduleEvaluation();
  }
})();
